import { userService } from "../services";
import type {
  LoginUserPayload,
  SessionInfoPayload,
  CheckSessionPayload,
  GetAllUsersPayload,
} from "../services/user.service";
import { alertActions } from "./";
import { history } from "../helpers";
import { userConstants } from "../constants";
import { setGlobalAlert } from "../store/globalAlertSlice";

import {
  addBusinessId,
  addLogedInRole,
  addDeviceToken,
  addSessionId,
  addLogedInUsersDetails,
  reset as resetLoginDetails,
} from "../actions/logindetails.actions";
import { setCredentials, setSession, logout as authLogout } from "../store/authSlice";
import { setRole } from "../store/roleSlice";
import { setPermissions } from "../store/permissionSlice";
import {
  setUserDetails,
  setSelectedBusinessId,
  setProfilePicture,
} from "../store/featureAccessSlice";
import { clearPersistedSessionStorage } from "../store/clearPersistedSession";
import { setFirstLoadDone } from "../store/casesClientSearchSlice";
import { sessionData } from "../helpers/session-data";
import {
  passwordLoginCredentials,
  shouldCreateDeviceSession,
  shouldLogoutAfterSessionCheckError,
} from "../utils/session/deviceSessionBootstrap";

export const userActions = {
  login,
  logout,
  getAll,
  createSession,
  checkSessionValidity,
  refreshSessionToken,
  checkSessionValidityWithValues,
};

function clearClientSessionState(dispatch) {
  dispatch(authLogout());
  clearPersistedSessionStorage();
}

function login(username, password) {
  return (dispatch) => {
    dispatch(alertActions.clear());
    dispatch(request({ username }));

    userService.login(username, password).then(
      (user: LoginUserPayload) => {
        dispatch(success({ username: username, token: user.key }));
        dispatch(resetLoginDetails());
        dispatch(setFirstLoadDone(false));
        dispatch(setCredentials(passwordLoginCredentials(username, user.key)));
        history.push("/");
      },
      (error) => {
        dispatch(failure(error));
        dispatch(alertActions.error(error));
      }
    );
  };

  function request(user) {
    return { type: userConstants.LOGIN_REQUEST, user };
  }
  function success(user) {
    return { type: userConstants.LOGIN_SUCCESS, user };
  }
  function failure(error) {
    return { type: userConstants.LOGIN_FAILURE, error };
  }
}

function createSession(device_ip, device_info) {
  return (dispatch) => {
    dispatch(request());
    userService.createSession(device_ip, device_info).then(
      (sessionInfo: SessionInfoPayload) => {
        dispatch(success(sessionInfo));
        if (sessionInfo !== undefined) {
          const { device_token, session_id } = sessionInfo.data.session_info;
          dispatch(addDeviceToken(device_token));
          dispatch(addSessionId(session_id));
          dispatch(setSession({ deviceToken: device_token, sessionId: session_id }));
          window.dispatchEvent(new CustomEvent("sessionDataChanged"));
          dispatch(
            userActions.getAll(
              sessionInfo.data.session_info.device_token,
              sessionInfo.data.session_info.session_id
            )
          );
        }
      },
      (error) => dispatch(failure(error))
    );
  };
  function request() {
    return { type: userConstants.SESSION_REQUEST };
  }
  function success(sessionInfo) {
    return { type: userConstants.SESSION_SUCCESS, sessionInfo };
  }
  function failure(error) {
    return { type: userConstants.SESSION_FAILURE, error };
  }
}

function checkSessionValidity() {
  return (dispatch) => {
    const { device_token, session_id } = sessionData();
    if (shouldCreateDeviceSession(device_token, session_id)) {
      return;
    }
    dispatch(request());
    userService.checkSessionValidity().then(
      async (checkSessionInfo: CheckSessionPayload) => {
        if (checkSessionInfo.success === true) {
          dispatch(success(checkSessionInfo));
          if (
            checkSessionInfo &&
            checkSessionInfo.data &&
            checkSessionInfo.data.session_info &&
            checkSessionInfo.data.session_info.session_id_refreshed === true
          ) {
            const { device_token, session_id } = checkSessionInfo.data.session_info;
            await dispatch(addDeviceToken(device_token));
            await dispatch(addSessionId(session_id));
            await dispatch(setSession({ deviceToken: device_token, sessionId: session_id }));
            window.dispatchEvent(new CustomEvent("sessionDataChanged"));
          }
          if (checkSessionInfo.data.session_info.force_login === true) {
            dispatch(
              setGlobalAlert({
                message: "Safely logged out due to inactivity. Please login to continue.",
                dismissible: false,
              })
            );
            setTimeout(() => {
              dispatch(userActions.logout());
            }, 100);
          }
        } else if (checkSessionInfo.success === false) {
          if (
            checkSessionInfo.meta &&
            checkSessionInfo.meta.input &&
            checkSessionInfo.meta.input.username
          ) {
            dispatch(
              setGlobalAlert({
                message: "Safely logged out due to inactivity. Please login to continue.",
                dismissible: false,
              })
            );
            setTimeout(() => {
              dispatch(userActions.logout());
            }, 100);
          } else {
          }
        }
      },
      (error) => {
        dispatch(failure(error));
        const message = error instanceof Error ? error.message : String(error ?? "");
        const missingDeviceSession = /session id and device token are required/i.test(message);
        if (!shouldLogoutAfterSessionCheckError(!missingDeviceSession)) {
          return;
        }
        dispatch(
          setGlobalAlert({
            message: "Your session has expired or could not be verified. Please log in again.",
            dismissible: false,
          })
        );
        setTimeout(() => {
          dispatch(userActions.logout());
        }, 100);
      }
    );
  };
  function request() {
    return { type: userConstants.SESSION_REQUEST };
  }
  function success(sessionInfo) {
    return { type: userConstants.SESSION_SUCCESS, sessionInfo };
  }
  function failure(error) {
    return { type: userConstants.SESSION_FAILURE, error };
  }
}

function checkSessionValidityWithValues(device_token, session_id) {
  return (dispatch) => {
    dispatch(request());
    userService.checkSessionValidityWithValues(device_token, session_id).then(
      async (checkSessionInfo: CheckSessionPayload) => {
        dispatch(success(checkSessionInfo));
        if (checkSessionInfo.success === true) {
          if (
            checkSessionInfo.data.session_info.is_active === true &&
            checkSessionInfo.data.session_info.session_id_refreshed === true
          ) {
            const { device_token, session_id } = checkSessionInfo.data.session_info;
            await dispatch(addDeviceToken(device_token));
            await dispatch(addSessionId(session_id));
            await dispatch(setSession({ deviceToken: device_token, sessionId: session_id }));
          } else if (checkSessionInfo.data.session_info.is_active === false) {
            dispatch(
              setGlobalAlert({
                message: "Safely logged out due to inactivity. Please login to continue.",
                dismissible: false,
              })
            );
            setTimeout(() => {
              dispatch(userActions.logout());
            }, 100);
          }
        } else if (checkSessionInfo.success === false) {
          dispatch(
            setGlobalAlert({
              message: "Safely logged out due to inactivity. Please login to continue.",
              dismissible: false,
            })
          );
          setTimeout(() => {
            dispatch(userActions.logout());
          }, 100);
        }
      },
      (error) => {
        dispatch(failure(error));
        dispatch(
          setGlobalAlert({
            message: "Your session has expired or could not be verified. Please log in again.",
            dismissible: false,
          })
        );
        setTimeout(() => {
          dispatch(userActions.logout());
        }, 100);
      }
    );
  };
  function request() {
    return { type: userConstants.SESSION_REQUEST };
  }
  function success(sessionInfo) {
    return { type: userConstants.SESSION_SUCCESS, sessionInfo };
  }
  function failure(error) {
    return { type: userConstants.SESSION_FAILURE, error };
  }
}

function refreshSessionToken(device_token, session_id) {
  return (dispatch) => {
    dispatch(request());
    userService.refreshSessionToken(device_token, session_id).then(
      (sessionRefresh) => dispatch(success(sessionRefresh)),
      (error) => dispatch(failure(error))
    );
  };
  function request() {
    return { type: userConstants.SESSION_REQUEST };
  }
  function success(sessionRefresh) {
    return { type: userConstants.SESSION_SUCCESS, sessionRefresh };
  }
  function failure(error) {
    return { type: userConstants.SESSION_FAILURE, error };
  }
}

function logout() {
  return (dispatch) => {
    dispatch(request());
    userService.logout().then(
      (_user) => {
        history.push("/");
        clearClientSessionState(dispatch);
      },
      (error) => {
        dispatch(failure(error));
        clearClientSessionState(dispatch);
        history.push("/");
      }
    );
  };

  function request() {
    return { type: userConstants.LOGOUT };
  }
  function failure(error) {
    return { type: userConstants.LOGIN_FAILURE, error };
  }
}

function getAll(device_token, session_id) {
  return (dispatch) => {
    dispatch(request());
    userService.getAll(device_token, session_id).then(
      async (users: GetAllUsersPayload) => {
        const details = users?.data?.user_details;
        dispatch(success(users));
        if (users !== undefined && details) {
          const role = details.role;
          const rawBusinessId =
            role === "business-user" && details.business
              ? details.business.business_id
              : role === "client" && details.business_link
                ? details.business_link.business_id
                : null;
          const businessId = rawBusinessId !== null ? String(rawBusinessId) : null;

          dispatch(addLogedInRole(role));
          await dispatch(addLogedInUsersDetails(details));
          if (businessId) dispatch(addBusinessId(businessId));

          dispatch(
            setCredentials({
              userId:
                details.user_id !== undefined && details.user_id !== null
                  ? String(details.user_id)
                  : null,
              deviceToken: device_token,
              sessionId: session_id,
              businessId,
            })
          );
          dispatch(setRole(role));
          dispatch(setPermissions(details.user_permissions || {}));
          dispatch(
            setUserDetails({
              ...details,
              user_id:
                details.user_id !== undefined && details.user_id !== null
                  ? String(details.user_id)
                  : undefined,
              business: details.business
                ? {
                    ...details.business,
                    business_id:
                      details.business.business_id !== undefined &&
                      details.business.business_id !== null
                        ? String(details.business.business_id)
                        : undefined,
                  }
                : undefined,
              business_link: details.business_link
                ? {
                    ...details.business_link,
                    business_id:
                      details.business_link.business_id !== undefined &&
                      details.business_link.business_id !== null
                        ? String(details.business_link.business_id)
                        : undefined,
                  }
                : undefined,
            })
          );
          if (details.profile_picture) dispatch(setProfilePicture(details.profile_picture));
          if (businessId) dispatch(setSelectedBusinessId(businessId));
        }
        window.dispatchEvent(new CustomEvent("sessionDataChanged"));
      },
      (error) => {
        dispatch(failure(error));
        dispatch(alertActions.error(error));
        dispatch(
          setGlobalAlert({
            message: "Session data could not be loaded. Please login again.",
            dismissible: false,
          })
        );
        setTimeout(() => {
          clearClientSessionState(dispatch);
          history.push("/");
        }, 100);
      }
    );
  };

  function request() {
    return { type: userConstants.GETALL_REQUEST };
  }
  function success(users) {
    return { type: userConstants.GETALL_SUCCESS, users };
  }
  function failure(error) {
    return { type: userConstants.GETALL_FAILURE, error };
  }
}
